import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, FileText, Languages, Download, Cpu, Eye, Shield, Zap, Star, Check } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const FEATURES = [
    {
        icon: FileText,
        title: "Çoklu Format Desteği",
        desc: "PDF, DOCX, EPUB ve TXT dosyalarınızı yükleyin. Aynı formatta veya farklı bir formatta çıktı alın.",
        badge: "4 Format",
        color: "blue",
    },
    {
        icon: Languages,
        title: "100+ Dil",
        desc: "Dünya genelinde yaygın kullanılan tüm dillere çeviri yapabilirsiniz. Türkçe, İngilizce, Almanca, Japonca ve çok daha fazlası.",
        badge: "100+ Dil",
        color: "purple",
    },
    {
        icon: Eye,
        title: "OCR Desteği",
        desc: "Taranmış ve görsel tabanlı PDF dosyalarında bile metin çıkarımı yapabilir, çevirebilirsiniz.",
        badge: "Yeni",
        color: "amber",
    },
    {
        icon: Zap,
        title: "Hızlı İşlem",
        desc: "Küçük belgeler dakikalar içinde tamamlanır. Büyük dosyalar arka planda işlenir, e-posta ile bildirim alırsınız.",
        badge: "Hızlı",
        color: "green",
    },
    {
        icon: Shield,
        title: "Güvenli & Gizli",
        desc: "Belgeleriniz 24 saat sonra otomatik silinir. SSL şifrelemesi ve güvenli altyapı ile %100 gizlilik.",
        badge: "SSL",
        color: "slate",
    },
    {
        icon: Download,
        title: "Kolay İndirme",
        desc: "Çevrilen dosyalarınızı profil sayfanızdan 24 saat boyunca istediğiniz zaman indirebilirsiniz.",
        badge: "24 Saat",
        color: "indigo",
    },
];

const colorMap = {
    blue: { bg: "bg-blue-50", icon: "bg-blue-100 text-blue-600", badge: "bg-blue-100 text-blue-700" },
    purple: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", badge: "bg-indigo-100 text-indigo-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", badge: "bg-amber-100 text-amber-700" },
    green: { bg: "bg-green-50", icon: "bg-green-100 text-green-600", badge: "bg-green-100 text-green-700" },
    slate: { bg: "bg-slate-50", icon: "bg-slate-200 text-slate-600", badge: "bg-slate-100 text-slate-700" },
    indigo: { bg: "bg-cyan-50", icon: "bg-cyan-100 text-cyan-600", badge: "bg-cyan-100 text-cyan-700" },
};

const COMPARE = [
    { feature: "PDF Çevirisi", us: true, rest: false },
    { feature: "DOCX / EPUB Desteği", us: true, rest: false },
    { feature: "OCR (Taranmış Belgeler)", us: true, rest: false },
    { feature: "24 Saat Dosya Saklama", us: true, rest: true },
    { feature: "100+ Dil", us: true, rest: false },
    { feature: "E-Posta Bildirimi", us: true, rest: false },
    { feature: "Görsel Koruma", us: true, rest: false },
];

const Features = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Özellikler — CeviriPro'nun Güçlü Çeviri Araçları"
                description="CeviriPro özellikleri: PDF, DOCX, EPUB çevirisi, OCR teknolojisi, format korumalı çeviri, 100+ dil desteği ve güvenli belge işleme. Rakiplere göre detaylı karşılaştırma."
                canonical="/ozellikler"
            />
            {/* Nav */}
            <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-blue-600 to-green-600 p-2 rounded-xl">
                            <Globe className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 text-lg">CeviriPro</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login" className="text-slate-600 hover:text-blue-900 text-sm font-medium transition-colors">Giriş Yap</Link>
                        <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all">Ücretsiz Başla</Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-green-50 py-20 px-4 text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-6">
                    <Cpu className="w-8 h-8 text-blue-600" />
                </div>
                <span className="inline-block px-4 py-1.5 bg-white border border-blue-200 text-blue-600 text-sm font-semibold rounded-full mb-4">Özellikler</span>
                <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
                    Doküman Çevirisi,<br />
                    <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Bir Sonraki Seviyede</span>
                </h1>
                <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    CeviriPro ile belgelerinizi saniyeler içinde dünyanın herhangi bir diline çevirin. Güvenli, hızlı ve doğru.
                </p>
            </section>

            {/* Features Grid */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <h2 className="text-3xl font-bold text-slate-900 mb-3">Neler Sunuyoruz?</h2>
                        <p className="text-slate-500 text-lg">Her ihtiyacınıza yönelik özellikler.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {FEATURES.map(({ icon: Icon, title, desc, badge, color }, i) => {
                            const c = colorMap[color];
                            return (
                                <div key={i} className={`rounded-3xl p-7 ${c.bg} hover:shadow-lg transition-all`}>
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${c.icon}`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.badge}`}>{badge}</span>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Compare Table */}
            <section className="py-20 px-4 bg-slate-50">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-slate-900 mb-3">Neden CeviriPro?</h2>
                        <p className="text-slate-500">Rakiplerimizle karşılaştırın.</p>
                    </div>
                    <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-slate-200">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="text-left p-5 text-slate-700 font-semibold">Özellik</th>
                                    <th className="p-5 text-center">
                                        <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent font-black text-lg">CeviriPro</span>
                                    </th>
                                    <th className="p-5 text-center text-slate-500 font-semibold">Diğerleri</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARE.map(({ feature, us, rest }, i) => (
                                    <tr key={i} className="border-b border-slate-50 last:border-0">
                                        <td className="p-5 text-slate-700">{feature}</td>
                                        <td className="p-5 text-center">
                                            {us ? <Check className="w-6 h-6 text-green-500 mx-auto" /> : <span className="text-slate-300 text-xl">—</span>}
                                        </td>
                                        <td className="p-5 text-center">
                                            {rest ? <Check className="w-6 h-6 text-slate-400 mx-auto" /> : <span className="text-red-300 text-xl font-bold">✕</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4 text-center">
                <Star className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Hemen Deneyin</h2>
                <p className="text-slate-500 text-lg mb-8">Ücretsiz kaydolun, ilk çevirinizi hemen yapın.</p>
                <Link to="/register" className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white text-lg font-bold rounded-2xl hover:shadow-xl transition-all hover:-translate-y-0.5">
                    Ücretsiz Başla
                </Link>
            </section>

            <Footer />
        </div>
    );
};

export default Features;
